<template>
    <SettingsSection cssClass="account-details-settings" :loading="loading">
        <template #title>
            <h2>{{ $t('views.account_subscriptions.subscription_settings_title') }}</h2>
        </template>

        <template #content>
            <div class="products-type-1" :class="[subscription.status === 'canceled' ? 'error' : subscription.status === 'new' ? 'success' : '']">
                <div class="left">
                    <div class="icon-card-pos"></div>
                </div>
                <div class="right">
                    <div class="name">{{title}}</div>
                    <div class="price" v-if="subscription && price">{{price}}</div>
                    <div class="description">{{description}}</div>
                    <div class="notice" v-if="notice.label" :class="notice.type">{{$t(notice.label, notice.translationLabels)}}</div>
                </div>
            </div>
            <router-link :to="{ name: 'subscription-change' }" v-if="subscription.order_id && !manualProduct && subscription.status !== 'canceled' && subscription.status !== 'new'" class="btn-1 btn-block btn-m">{{$t('views.account_subscriptions.button_switch_subscriptions')}}</router-link>
            <router-link :to="{ name: 'subscription-order' }" v-else-if="(!manualProduct || manualProductUpsellPossible) && subscription.status !== 'canceled' && subscription.status !== 'new'" class="btn-1 btn-block btn-m">{{$t('views.account_subscriptions.button_new_subscriptions')}}</router-link>
            <router-link :to="{ name: 'subscription-order' }" v-else-if="subscription.status === 'canceled' && subscription.status !== 'new'" class="btn-1 btn-block btn-m">{{$t('views.account_subscriptions.button_new_subscriptions_alt')}}</router-link>
            <div @click="cancelSubscriptionPopup" v-if="subscription.order_id && !manualProduct && subscription.status !== 'canceled'" class="btn-6 btn-block btn-m">{{$t('views.account_subscriptions.button_cancel_subscriptions')}}</div>
        </template>
    </SettingsSection>
</template>

<script>
    import i18n from "@/plugins/i18n";
    import { mapState, mapActions } from 'pinia';
    import { useProductsStore } from '@/stores/products';
    import { usePopupStore } from '@/stores/utils/popup';
    import { useToastStore } from '@/stores/utils/toast';
    import { useAccountStore } from '@/stores/account';
    import { timestampToDisplayDate } from '@/utils/dates';
    import { formatNumber } from '@/utils/formatting';
    import SettingsSection from '@/components/accountSettings/Section';
    
    export default {
        name: 'InvoiceSettings',
        components: {
            SettingsSection
        },
        data() {
            return {
                loadingSubscriptions: false
            }
        },
        computed: {
            ...mapState(useProductsStore, { loadingProducts: 'loading' }),
            ...mapState(useAccountStore, ['subscriptions', 'hasSubscriptions']),
            ...mapState(useAccountStore, {
                activeProduct: 'product',
                activeProductType: 'productType',
                paid_to_time: 'paid_to_time'
            }),
            accountStore() {
                return useAccountStore();
            },
            toastStore() {
                return useToastStore();
            },
            currentProduct() {
                return this.getProduct(this.subscription?.product_main || this.activeProductType);
            },
            manualProduct() {
                return (this.subscription?.product || this.activeProduct || '').includes('ma_');
            },
            manualProductUpsellPossible() {
                return !this.subscription && this.activeProduct.includes('_pch');
            },
            loading() {
                return this.loadingSubscriptions || this.loadingProducts;
            },
            subscription() {
                return this.subscriptions?.length > 0 ? this.subscriptions[0] : false;
            },
            title() {
                return this.manualProductUpsellPossible ? i18n.global.t('fields.options_subscriptions.options.custom_up.text') :
                    this.manualProduct ? i18n.global.t('fields.options_subscriptions.options.custom.text') :
                    i18n.global.locale === 'nl' ? this.currentProduct.title_nl || this.currentProduct.title_en :
                    this.currentProduct.title_en || this.currentProduct.title_nl || '';
            },
            description() {
                return this.manualProductUpsellPossible ? i18n.global.t('fields.options_subscriptions.options.custom_up.description') :
                    this.manualProduct ? i18n.global.t('fields.options_subscriptions.options.custom.description') :
                    i18n.global.locale === 'nl' ? this.currentProduct.description_nl || this.currentProduct.description_en :
                    this.currentProduct.description_en || this.currentProduct.description_nl || '';
            },
            price() {
                return this.manualProduct ? false :
                    (process.env.VUE_APP_PRODUCT_TRIAL ? this.currentProduct.product_main === process.env.VUE_APP_PRODUCT_TRIAL : this.currentProduct.product_main === 'trial') ? false :
                    (process.env.VUE_APP_PRODUCT_FREE ? this.currentProduct.product_main === process.env.VUE_APP_PRODUCT_FREE : this.currentProduct.product_main === 'free') ? i18n.global.t('common.price_monthly', { value: formatNumber(this.subscription.price) }) :
                    i18n.global.t('common.price_monthly_vat', { value: formatNumber(this.subscription.price) });
            },
            notice() {
                if(this.manualProductUpsellPossible) {
                    return {
                        label: null
                    }
                } else if(this.manualProduct) {
                    return {
                        label: 'fields.options_subscriptions.notice.duration_unknown'
                    }
                } else if(process.env.VUE_APP_PRODUCT_FREE ? this.currentProduct.product_main === process.env.VUE_APP_PRODUCT_FREE : this.currentProduct.product_main === 'free') {
                    return {
                        type: 'error',
                        label: 'fields.options_subscriptions.notice.duration_unlimited'
                    }
                } else if(process.env.VUE_APP_PRODUCT_TRIAL ? this.currentProduct.product_main === process.env.VUE_APP_PRODUCT_TRIAL : this.currentProduct.product_main === 'trial') {
                    return {
                        label: 'fields.options_subscriptions.notice.duration_canceled',
                        translationLabels: { renewalDate: timestampToDisplayDate(this.paid_to_time, false, true) }
                    }
                } else if(this.subscription.canceled_time) {
                    return {
                        type: 'error',
                        label: 'fields.options_subscriptions.notice.duration_canceled',
                        translationLabels: { renewalDate: timestampToDisplayDate(this.subscription.paid_to_time || this.subscription.canceled_time, false, true) }
                    }
                } else if (this.subscription.paid_to_time) {
                    return {
                        label: 'fields.options_subscriptions.notice.duration_renewal',
                        translationLabels: { renewalDate: timestampToDisplayDate(this.subscription.paid_to_time, false, true) }
                    }
                } else if (this.subscription.status === 'new') {
                    // This may contain exceptions for different apps/products.
                    if(process.env.VUE_APP_TYPE === 'bedrijfsdata') {
                        if(this.currentProduct.product_main === 'frl') {
                            return {
                                type: 'success',
                                label: 'fields.options_subscriptions.notice.downgrade_in_progress',
                            }
                        } else {
                            return {
                                type: 'success',
                                label: 'fields.options_subscriptions.notice.upgrade_in_progress',
                            }
                        }
                    } else {
                        return {
                            type: 'success',
                            label: 'fields.options_subscriptions.notice.updowngrade_in_progress',
                        }
                    }
                } else {
                    return {
                        label: 'fields.options_subscriptions.notice.duration_limited',
                    }
                }
            },
        },
        methods: {
            ...mapActions(useProductsStore, ['getProduct', 'getProducts']),
            formatNumber,
            async fetchSubscriptions() {
                this.loadingSubscriptions = true;
                await this.accountStore.getSubscriptions();
                this.loadingSubscriptions = false;
            },
            cancelSubscriptionPopup() {
                usePopupStore().showPopup('confirm', {
                    title: 'popups.confirm_subscription_delete.title',
                    description: this.subscription.paid_to_time ? 'popups.confirm_subscription_delete.description' : 'popups.confirm_subscription_delete.description_alt',
                    btnConfirm: "popups.confirm_subscription_delete.button_confirm",
                    btnCancel: "popups.confirm_subscription_delete.button_cancel",
                    confirmFunction: this.deleteSubscription,
                    danger: true,
                    translationPlaceholders: {
                        description: {
                            renewalDate: this.subscription.paid_to_time ? timestampToDisplayDate(this.subscription.paid_to_time, false) : '[?]',
                        }
                    }
                });
            },
            async deleteSubscription() {
                const accountStore = useAccountStore();
                const response = await accountStore.deleteSubscription(this.subscription.order_id);

                if(response) {
                    this.toastStore.addToast(i18n.global.t('toasts.success.subscription_deleted'), 'success');
                } else {
                    this.toastStore.addToast(i18n.global.t('toasts.error.subscription_not_deleted'), 'error');
                }
            }
        },
        mounted() {
            this.getProducts();
            this.fetchSubscriptions();
        }
    }
</script>